import { BigNumber } from "bignumber.js";
import { cn, delay, PropsWithClassName } from "kz-ui-sdk";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styles from "./SpinningNumber.module.css";
import toSpinningDigits, { ANIMATION_DEFAULTS, BOUNCE_CURVES, DIGITS } from "./utils";

export interface SpinningNumberRef {
  startSpin: (value?: number | BigNumber) => void;
  stopSpin?: () => void;
  hideSpin?: () => void;
}

interface SpinningNumberProps extends PropsWithClassName {
  initialValue?: number | BigNumber;
  autoStart?: boolean;
  animate?: {
    spinDuration?: number;
    stopDelay?: number;
    spinningHideMs?: number;
  };
  style?: {
    containerHeight?: number;
    containerWidth?: number;
    digitWidth?: number;
    className?: string;
    digitClassName?: string;
    wheelClassName?: string;
    zIndex?: number;
    circularClipRadius?: number;
    font?: {
      size?: number;
      weight?: number;
      letterSpacing?: string;
    };
  };
  onComplete?: () => void;
}

const SpinningNumber = forwardRef<SpinningNumberRef, SpinningNumberProps>(
  ({ initialValue, autoStart = false, animate = {}, style = {}, className, onComplete }, ref) => {
    const { size: fontSize = 46, weight: fontWeight = "bold", letterSpacing = "-2.3px" } = style.font || {};
    const {
      spinDuration = ANIMATION_DEFAULTS.spinDuration,
      stopDelay = ANIMATION_DEFAULTS.stopDelay,
      spinningHideMs = ANIMATION_DEFAULTS.spinningHideMs,
    } = animate;
    const {
      containerHeight = 120,
      containerWidth = "auto",
      digitWidth = 29,
      digitClassName = "",
      wheelClassName = "",
      zIndex,
      circularClipRadius,
    } = style;

    const containerHeightNum = typeof containerHeight === "string" ? parseFloat(containerHeight) : containerHeight;
    const digitHeight = containerHeightNum / 2.6;
    const wheelOffset = digitHeight * 2.8;
    const digitWidthNum = typeof digitWidth === "string" ? parseFloat(digitWidth) : digitWidth;
    const punctuationMargin = digitWidthNum * -0.3;
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [currentValue, setCurrentValue] = useState<number | BigNumber | undefined>(initialValue);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isClipExpanding, setIsClipExpanding] = useState(false);
    const layoutNumberRef = useRef<HTMLDivElement | null>(null);
    const [targetValue, setTargetValue] = useState<number | BigNumber | undefined>(undefined);
    const animations = useRef<{ spin: () => void; stop: (delay: number) => void }[]>([]);
    const lastDigitCompleted = useRef(false);
    const displayValue = currentValue !== undefined ? currentValue : initialValue !== undefined ? initialValue : 0;
    const valueForFormatting = isAnimating && targetValue !== undefined ? targetValue : displayValue;

    const formattedDisplayValue = useMemo(() => {
      const formatted = toSpinningDigits(
        typeof valueForFormatting === "number" ? valueForFormatting : valueForFormatting.toNumber(),
      );
      // Split the K suffix if present
      const match = formatted.match(/^(.+?)K$/);
      return match ? match[1] : formatted;
    }, [valueForFormatting]);

    const hasKSuffix = useMemo(() => {
      const formatted = toSpinningDigits(
        typeof valueForFormatting === "number" ? valueForFormatting : valueForFormatting.toNumber(),
      );
      return formatted.endsWith("K");
    }, [valueForFormatting]);

    // start spinning animation
    const startAnimation = useCallback(
      (ref: HTMLDivElement | null, index: number, value: number | BigNumber | undefined, onComplete?: () => void) => {
        const factor = 5 + Math.floor(Math.random() * 10);
        const section = 100 / DIGITS.length;
        let spinTimer: number;

        // start from a random number
        const randomStart = Math.floor(Math.random() * DIGITS.length);
        if (ref) {
          ref.style.transform = `translateY(-${randomStart * section}%)`;
          ref.style.opacity = "1";
        }

        const spin = () => {
          if (ref) ref.style.transition = "";
          let value = randomStart * section;

          const animate = () => {
            if (value <= -(section * DIGITS.length)) {
              value = 0;
            }
            value -= section / factor;
            if (ref) {
              ref.style.transform = `translateY(${-value}%)`;
              ref.style.opacity = "1";
            }
            spinTimer = requestAnimationFrame(animate);
          };
          spinTimer = requestAnimationFrame(animate);
        };

        // stop spinning animation
        const stopAnimation = (delay: number | undefined) => {
          setTimeout(() => {
            cancelAnimationFrame(spinTimer);
            if (ref && value !== undefined) {
              const formattedValue = toSpinningDigits(typeof value === "number" ? value : value.toNumber());
              const targetNumber = formattedValue[index];
              const targetIndex = DIGITS.length - DIGITS.indexOf(targetNumber);

              // Calculate wheel dimensions
              const wheelTotalHeight = digitHeight * DIGITS.length;
              const section = 100 / DIGITS.length;

              // Old calculation positions target digit at bottom: section * targetIndex - section
              // This positions the digit so its top aligns with the bottom of visible area
              const oldTargetPosition = section * targetIndex - section;

              // targetIndex is 1-based from bottom, so digit position in DIGITS array is:
              // digitIndexInArray = DIGITS.length - targetIndex
              // Position from top of wheel (as percentage): digitIndexInArray * section
              // Center of digit: digitIndexInArray * section + section / 2
              const digitIndexInArray = DIGITS.length - targetIndex;
              const targetDigitTopPercent = digitIndexInArray * section;
              const targetDigitCenterPercent = targetDigitTopPercent + section / 2;

              // The wheel container has translateY(calc(-100% + wheelOffset))
              // The old position positions the digit, but we need to center it properly
              // Calculate the adjustment needed: move up by half digit height to center the digit
              // But account for the fact that we might need slightly less adjustment
              // to prevent showing part of the next number above
              const centerAdjustmentPixels = digitHeight / 2;
              const centerAdjustmentPercent = (centerAdjustmentPixels / wheelTotalHeight) * 100;

              // Adjust upward from old position to center the digit
              // Using 0.7 multiplier to fine-tune and prevent showing next number
              const targetPosition = oldTargetPosition - centerAdjustmentPercent * 0.7;

              // get random bounce curve and duration
              const bounceCurveKeys = Object.keys(BOUNCE_CURVES) as (keyof typeof BOUNCE_CURVES)[];
              const randomBounceCurve =
                BOUNCE_CURVES[bounceCurveKeys[Math.floor(Math.random() * bounceCurveKeys.length)]];
              const bounceDuration =
                Math.random() * (ANIMATION_DEFAULTS.bounceMaxDuration - ANIMATION_DEFAULTS.bounceMinDuration) +
                ANIMATION_DEFAULTS.bounceMinDuration;

              // reset the position of the wheel
              ref.style.opacity = "0";
              ref.style.transform = `translateY(-${wheelOffset}px)`;
              ref.style.transitionDuration = "0.1s";
              ref.style.transitionProperty = "transform";

              // scroll down to the target position with bounce effect
              setTimeout(() => {
                ref.style.transitionDuration = `${bounceDuration}s`;
                ref.style.transitionTimingFunction = randomBounceCurve;
                ref.style.transform = `translateY(${targetPosition}%)`;
                ref.style.opacity = "1";

                // Call completion callback after bounce animation completes
                setTimeout(() => {
                  if (onComplete) {
                    onComplete();
                  }
                }, bounceDuration * 1000);

                if (formattedValue[index] === "-") {
                  ref.style.transition = "transform 1.5s ease-out";
                  ref.style.opacity = "0";
                }
              }, 100);
            }
          }, delay);
        };

        return { spin, stop: stopAnimation };
      },
      [wheelOffset, digitHeight],
    );

    // start spinning with value
    const startSpin = useCallback(
      (value?: number | BigNumber) => {
        const targetValue = value !== undefined ? value : currentValue;
        if (targetValue === undefined) return;

        setTargetValue(targetValue);
        setIsVisible(true);
        setIsAnimating(true);
        setIsHighlighted(false);
        lastDigitCompleted.current = false;

        const formattedValue = toSpinningDigits(typeof targetValue === "number" ? targetValue : targetValue.toNumber());
        const amountStrLength = formattedValue.length;

        // defer animation start until DOM updates for new wheels
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            animations.current = Array.from({
              length: amountStrLength,
            }).map((_, index) => {
              const reverseIndex = amountStrLength - index - 1;
              const wheelRef = document.getElementById(
                ["spinning-number-wheel", reverseIndex].join("-"),
              ) as HTMLDivElement | null;

              const onCompleteAnimation =
                reverseIndex === 0
                  ? () => {
                      if (!lastDigitCompleted.current) {
                        lastDigitCompleted.current = true;
                        setCurrentValue(targetValue);
                        setTargetValue(undefined);

                        // Start clip expansion transition
                        if (circularClipRadius) {
                          setIsClipExpanding(true);
                          // Let scale and margin transitions run first
                          setTimeout(() => {
                            // After scale/margin transition, remove clip
                            setIsAnimating(false);
                            setIsHighlighted(true);
                          }, 350);
                          // Then remove expanding state after clip transition completes
                          setTimeout(() => {
                            setIsClipExpanding(false);
                            onComplete?.();
                          }, 650);
                        } else {
                          setIsAnimating(false);
                          setIsHighlighted(true);
                          setTimeout(() => {
                            onComplete?.();
                          }, 500);
                        }
                      }
                    }
                  : undefined;

              const animation = startAnimation(wheelRef, reverseIndex, targetValue, onCompleteAnimation);
              animation.spin();

              delay(spinDuration).then(() => animation.stop(index * stopDelay));

              return animation;
            });
          });
        });
      },
      [currentValue, startAnimation, spinDuration, stopDelay, onComplete, circularClipRadius],
    );

    const hideSpin = useCallback(() => {
      const element = layoutNumberRef.current;
      if (!element) return;
      // prepare transition - only fade, no movement
      element.style.transition = `opacity ${spinningHideMs}ms ease-out`;
      element.style.willChange = "opacity";
      // ensure starting point
      element.style.opacity = "1";
      requestAnimationFrame(() => {
        element.style.opacity = "0";
      });
    }, [spinningHideMs]);

    useImperativeHandle(
      ref,
      () => ({
        startSpin,
        hideSpin,
      }),
      [startSpin, hideSpin],
    );

    // auto start if enabled and initialValue is provided
    useEffect(() => {
      if (autoStart && initialValue !== undefined) {
        setIsVisible(true);
        startSpin(initialValue);
      }
    }, [autoStart, initialValue, startSpin]);

    // During spinning, clip bottom half to hide digits below center
    const isSpinningPhase = isAnimating && !isClipExpanding;

    return (
      <>
        <div
          className={cn(
            "absolute bottom-[-10px] flex items-baseline justify-center overflow-hidden transition-opacity duration-500",
            className,
          )}
          style={{
            height: containerHeight,
            width: containerWidth,
            opacity: isVisible ? 1 : 0,
            zIndex,
            clipPath:
              circularClipRadius && (isAnimating || isClipExpanding)
                ? isClipExpanding
                  ? `circle(200% at 50% 50%)`
                  : `circle(${circularClipRadius}px at 50% 50%)`
                : undefined,
            transition: isClipExpanding ? "clip-path 300ms ease-out" : undefined,
          }}
        >
          {/* Wrapper to hide bottom half of circle during spinning */}
          <div
            style={{
              clipPath: isSpinningPhase ? `inset(0 0 33% 0)` : undefined,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
            }}
          >
            <div
              className="transition-transform duration-500 will-change-transform"
              style={{
                transform: `translateY(calc(-100% + ${wheelOffset}px))`,
              }}
            >
              <div
                className="flex items-baseline"
                ref={layoutNumberRef}
              >
                <div>
                  {Array.from({
                    length: formattedDisplayValue.length,
                  }).map((_, wheelIndex) => {
                    const currentChar = formattedDisplayValue[wheelIndex] || "0";
                    const isPunctuation = [".", ","].includes(currentChar);
                    const isEmpty = currentChar === "-" || currentChar === undefined;
                    const isSpinning = isAnimating && !isClipExpanding;
                    // Extra negative margin to condense digits during spinning (accounts for 0.75 scale)
                    const spinCondenseMargin = -4;

                    // Collapse empty placeholder digits when not spinning (same timing as scale/margin)
                    const shouldCollapse = !isSpinning && isEmpty;

                    return (
                      <div
                        className={cn(
                          "inline-block animate-fade-in-3s overflow-hidden will-change-transform",
                          wheelClassName,
                        )}
                        style={{
                          maxWidth: shouldCollapse ? 0 : digitWidth + 16,
                          paddingLeft: shouldCollapse ? 0 : 8,
                          paddingRight: shouldCollapse ? 0 : 8,
                          marginLeft:
                            isAnimating || isHighlighted
                              ? isPunctuation
                                ? punctuationMargin - 8
                                : isSpinning
                                  ? -8 + spinCondenseMargin
                                  : shouldCollapse
                                    ? 0
                                    : -8
                              : 0,
                          marginRight:
                            isAnimating || isHighlighted
                              ? isPunctuation
                                ? punctuationMargin - 8
                                : isSpinning
                                  ? -8 + spinCondenseMargin
                                  : shouldCollapse
                                    ? 0
                                    : -8
                              : 0,
                          transition: !isSpinning
                            ? "max-width 300ms ease-out, padding 300ms ease-out, margin 300ms ease-out"
                            : undefined,
                        }}
                        key={["wheel", wheelIndex].join("-")}
                      >
                        <section>
                          <div
                            className={cn("absolute text-center")}
                            style={{
                              top: 0,
                              position: "relative",
                              width: digitWidth,
                            }}
                            id={["spinning-number-wheel", wheelIndex].join("-")}
                          >
                            {DIGITS.map((num) => (
                              <div
                                className={cn("animate-fade-in will-change-transform", digitClassName)}
                                key={["wheel", wheelIndex, "digit", num].join("-")}
                              >
                                <p
                                  className={cn("text-center", {
                                    [styles.digitHighlighted]: isHighlighted,
                                    [styles.digitNormal]: !isHighlighted,
                                  })}
                                  style={{
                                    fontSize: fontSize,
                                    fontWeight: fontWeight,
                                    letterSpacing: letterSpacing,
                                    transform: isSpinning ? "scale(0.75)" : "scale(1)",
                                    transition: !isSpinning ? "transform 300ms ease-out" : undefined,
                                  }}
                                >
                                  {num}
                                </p>
                              </div>
                            ))}
                          </div>
                        </section>
                      </div>
                    );
                  })}
                </div>
                {hasKSuffix && (
                  <div
                    className={cn("inline-block overflow-hidden transition-all duration-500", {
                      "!max-w-0": !isHighlighted,
                    })}
                    style={{
                      maxWidth: digitWidth + 2,
                      transform: "translateY(-28px)"
                    }}
                  >
                    <div
                      className={cn("absolute text-center")}
                      style={{
                        top: 0,
                        position: "relative",
                        width: digitWidth + 2,
                      }}
                    >
                      <div className="animate-fade-in will-change-transform">
                        <p
                          className={cn("text-center", {
                            [styles.digitHighlighted]: isHighlighted,
                            [styles.digitNormal]: !isHighlighted,
                            "opacity-0": !isHighlighted,
                            "opacity-100": isHighlighted,
                          })}
                          style={{
                            fontSize: fontSize,
                            fontWeight: fontWeight,
                            letterSpacing: letterSpacing,
                          }}
                        >
                          K
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);

SpinningNumber.displayName = "SpinningNumber";

export default SpinningNumber;
